import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import DDRDashboard, { parseDDRWorkbook } from "./DDRDashboard";

/* ══════════════════════════════════════════════════════════════════════
   /affordability — signed-in users only.

   Layout mirrors the RAG chat: a sidebar on the left (navigation, the
   published-workbook panel and the user footer) with the dashboard filling
   the rest. Anyone signed in can publish a newer ddr_outlets workbook from
   the sidebar; it goes to POST /api/affordability/upload and every visitor
   sees it on their next load. The previous file is kept as a version, so
   a bad upload can be rolled back from the version list.

   Publishing is limited to the emails listed in PUBLISHERS in
   backend/routes/affordability_routes.py (or the AFFORDABILITY_PUBLISHERS
   env var). This page asks GET /api/affordability/permissions and hides the
   upload panel for everyone else; the backend enforces it on every write.
   ══════════════════════════════════════════════════════════════════════ */

const BACKEND = process.env.REACT_APP_BACKEND || "http://localhost:8000";
const API = `${BACKEND}/api/affordability`;
const WORKBOOK = "ddr_outlets.xlsx";           // copy in /public, fallback only

/* Set false once the workbook is served from the backend and the copy has
   been deleted from /public. */
const ALLOW_PUBLIC_FALLBACK = true;

const LOGIN_PATH = "/";
const CHAT_PATH  = "/rag";
const TOKEN_KEY  = "konet_token";
const EXPIRY_KEY = "konet_token_expiry";
export const REDIRECT_KEY = "login_redirect_to";   // key CallbackPage reads

/* Theme — same palette as the chat app */
const T = {
  bg: "#faf8f3", bgAlt: "#f4f1ea", surface: "#ffffff",
  sidebar: "#f1ede4", border: "#e4ddce",
  text: "#1c1b18", textMid: "#55524b", textDim: "#8a857a",
  accent: "#c7432b", accentSoft: "#fbe9e3", accentDark: "#9d3522",
  pos: "#1A7A3A", neg: "#C42020",
  shadow: "0 1px 2px rgba(60,50,30,0.06), 0 2px 6px rgba(60,50,30,0.04)",
  shadowMd: "0 4px 14px rgba(60,50,30,0.08)",
  sans: "'Geist','Inter',-apple-system,BlinkMacSystemFont,sans-serif",
  radiusSm: 10,
};

/* ── Session ─────────────────────────────────────────────────────────── */
function isExpired(raw) {
  if (!raw) return false;
  const n = Number(raw);
  const ms = Number.isFinite(n) && n > 0 ? (n < 1e12 ? n * 1000 : n) : Date.parse(raw);
  return Number.isFinite(ms) && ms <= Date.now();
}

export function hasValidSession() {
  try {
    if (!localStorage.getItem(TOKEN_KEY)) return false;
    if (isExpired(localStorage.getItem(EXPIRY_KEY))) return false;
    return true;
  } catch { return false; }
}

export function consumePostLoginRedirect(fallback = CHAT_PATH) {
  try {
    const to = localStorage.getItem(REDIRECT_KEY);
    localStorage.removeItem(REDIRECT_KEY);
    if (to && to.startsWith("/") && !to.startsWith("//") && to !== "/") return to;
  } catch {}
  return fallback;
}

const authHeader = () => {
  try { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; }
  catch { return {}; }
};

function clearSession() {
  try { [TOKEN_KEY, EXPIRY_KEY, "konet_user", "konet_user_photo"].forEach(k => localStorage.removeItem(k)); } catch {}
}

const storedUser = () => {
  try { return JSON.parse(localStorage.getItem("konet_user")) || null; } catch { return null; }
};

/* ── XLSX from CDN (window.XLSX), as everywhere else in the app ──────── */
const XLSX_CDN = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
let xlsxPromise = null;
function loadXLSX() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = XLSX_CDN;
    el.async = true;
    el.onload = () => (window.XLSX ? resolve(window.XLSX)
      : reject(new Error("XLSX loaded but window.XLSX is undefined")));
    el.onerror = () => { xlsxPromise = null; reject(new Error("Could not load the XLSX library")); };
    document.head.appendChild(el);
  });
  return xlsxPromise;
}

/* ── Auth gate: token must exist, be unexpired and accepted by /api/auth/me ── */
export function RequireAuth({ children }) {
  const location = useLocation();
  const [st, setSt] = useState(() => (hasValidSession() ? "checking" : "out"));

  const verify = useCallback(async () => {
    if (!hasValidSession()) { setSt("out"); return; }
    try {
      const r = await fetch(`${BACKEND}/api/auth/me`, { headers: authHeader() });
      if (r.status === 401 || r.status === 403) { clearSession(); setSt("out"); return; }
      if (!r.ok) throw new Error(String(r.status));
      const body = await r.json().catch(() => ({}));
      if (body?.user) { try { localStorage.setItem("konet_user", JSON.stringify(body.user)); } catch {} }
      setSt("in");
    } catch {
      setSt(prev => (prev === "in" ? "in" : "offline"));
    }
  }, []);

  useEffect(() => {
    verify();
    const t = setInterval(verify, 5 * 60 * 1000);
    const onStorage = (e) => { if (e.key === TOKEN_KEY || e.key === EXPIRY_KEY) verify(); };
    window.addEventListener("storage", onStorage);
    return () => { clearInterval(t); window.removeEventListener("storage", onStorage); };
  }, [verify]);

  if (st === "out") {
    try { localStorage.setItem(REDIRECT_KEY, location.pathname + location.search); } catch {}
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }
  if (st === "checking") return <Splash title="Checking your sign-in…" />;
  if (st === "offline") {
    return (
      <Splash title="Can't reach the server to verify your sign-in"
        action={<Btn onClick={() => { setSt("checking"); verify(); }}>Try again</Btn>} />
    );
  }
  return children;
}

export default function AffordabilityPage(props) {
  return <RequireAuth><AffordabilityShell {...props} /></RequireAuth>;
}

/* ══════════════════════════════════════════════════════════════════════
   Shell: sidebar + dashboard
   ══════════════════════════════════════════════════════════════════════ */
function AffordabilityShell({ onAskAI }) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 820 : false));
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 820 : true));
  const [state, setState] = useState({ status: "loading" });
  const [meta, setMeta] = useState(null);
  const [versions, setVersions] = useState(null);      // null = not loaded
  const [canPublish, setCanPublish] = useState(false);
  const [busy, setBusy] = useState("");                 // "upload" | "rollback:<id>"
  const [notice, setNotice] = useState(null);           // { kind, text }
  const fileRef = useRef(null);
  const user = storedUser();

  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth < 820;
      setIsMobile(m);
      setSidebarOpen(!m);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* What is published, for the sidebar panel. Absent (404) is normal while
     the workbook is still a file on disk. */
  const refreshMeta = useCallback(async () => {
    try {
      const r = await fetch(`${API}/meta`, { headers: authHeader() });
      setMeta(r.ok ? await r.json() : null);
    } catch { setMeta(null); }
  }, []);

  /* Publishing is limited to the emails in PUBLISHERS on the backend; ask it
     who this user is rather than keeping a second list in the bundle. */
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/permissions`, { headers: authHeader() })
      .then(r => (r.ok ? r.json() : { canPublish: false }))
      .then(b => { if (!cancelled) setCanPublish(!!b.canPublish); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      let resp = await fetch(`${API}/workbook?t=${Date.now()}`, { headers: authHeader(), cache: "no-store" });
      if (resp.status === 401 || resp.status === 403) { clearSession(); window.location.replace(LOGIN_PATH); return; }
      if (resp.status === 404 && ALLOW_PUBLIC_FALLBACK) {
        resp = await fetch(`${process.env.PUBLIC_URL || ""}/${WORKBOOK}?t=${Date.now()}`);
      }
      if (!resp.ok) throw new Error(`Could not load the workbook (${resp.status}). Publish one from the sidebar.`);

      const buf = await resp.arrayBuffer();
      const head = new TextDecoder().decode(new Uint8Array(buf.slice(0, 64)));
      if (/^\s*<!doctype|^\s*<html/i.test(head)) {
        throw new Error("The workbook request returned an HTML page instead of the file.");
      }
      const XLSX = await loadXLSX();
      const data = parseDDRWorkbook(XLSX.read(buf, { type: "array" }), XLSX);
      if (!data) throw new Error("The workbook could not be parsed.");
      setState({ status: "ready", data });
    } catch (e) {
      setState({ status: "error", error: e.message });
    }
  }, []);

  useEffect(() => { load(); refreshMeta(); }, [load, refreshMeta]);

  /* Publish a new workbook: parse it here first, so a file that the
     dashboard can't read is never published to everyone. */
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
      setNotice({ kind: "err", text: "Pick an .xlsx, .xlsm or .xls file." });
      return;
    }
    setBusy("upload");
    setNotice(null);
    try {
      const XLSX = await loadXLSX();
      const parsed = parseDDRWorkbook(XLSX.read(await file.arrayBuffer(), { type: "array" }), XLSX);
      if (!parsed) throw new Error("This workbook doesn't look like a DDR outlets file — nothing was published.");

      const form = new FormData();
      form.append("file", file);
      form.append("note", `uploaded from the affordability page`);
      const r = await fetch(`${API}/upload`, { method: "POST", headers: authHeader(), body: form });
      if (r.status === 401 || r.status === 403) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "You don't have permission to publish this dashboard.");
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${r.status})`);
      }
      setState({ status: "ready", data: parsed });       // show it immediately
      setNotice({ kind: "ok", text: `${file.name} published — everyone sees it from their next load.` });
      setVersions(null);
      refreshMeta();
    } catch (err) {
      setNotice({ kind: "err", text: err.message });
    } finally {
      setBusy("");
    }
  };

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/versions`, { headers: authHeader() });
      setVersions(r.ok ? (await r.json()).versions || [] : []);
    } catch { setVersions([]); }
  }, []);

  const rollback = async (versionId) => {
    setBusy(`rollback:${versionId}`);
    setNotice(null);
    try {
      const r = await fetch(`${API}/rollback`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ versionId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Rollback failed (${r.status})`);
      }
      setNotice({ kind: "ok", text: `Restored ${versionId}.` });
      await Promise.all([load(), refreshMeta(), loadVersions()]);
    } catch (err) {
      setNotice({ kind: "err", text: err.message });
    } finally {
      setBusy("");
    }
  };

  const logout = () => { clearSession(); window.location.replace(LOGIN_PATH); };

  return (
    <div style={{ display: "flex", height: "100vh", width: "100%", overflow: "hidden",
      fontFamily: T.sans, background: T.bg, color: T.text, position: "relative" }}>

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(30,25,20,0.4)", zIndex: 40 }} />
      )}

      {/* ═══ SIDEBAR ═══ */}
      <aside style={{
        width: sidebarOpen ? 280 : 0, minWidth: sidebarOpen ? 280 : 0,
        background: T.sidebar, borderRight: `1px solid ${T.border}`,
        display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden",
        transition: "width 0.25s ease, min-width 0.25s ease",
        position: isMobile ? "fixed" : "relative", zIndex: 45, left: 0, top: 0,
      }}>
        <div style={{ padding: "18px 18px 14px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 11, flexShrink: 0,
            background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 17 }}>✦</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.4px", lineHeight: 1 }}>KOINE AI INSIGHTS </div>
            <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 3 }}>Affordability Dashboard</div>
          </div>
        </div>

        <div style={{ padding: "0 14px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
          <SideBtn onClick={() => navigate(CHAT_PATH)} icon="💬">Back to chat</SideBtn>
          <SideBtn active icon="📊">Affordability Dashboard</SideBtn>
        </div>

        {/* ─── Workbook panel ─── */}
        <div style={{ padding: "4px 14px 10px", overflowY: "auto", flex: "1 1 0", minHeight: 0 }}>
          <Label>Workbook</Label>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm, padding: 12, boxShadow: T.shadow }}>
            {meta ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, wordBreak: "break-all" }}>{meta.fileName || WORKBOOK}</div>
                <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 4, lineHeight: 1.5 }}>
                  v{meta.version}
                  {meta.publishedAt ? ` · ${new Date(meta.publishedAt).toLocaleString()}` : ""}
                  {meta.publishedBy ? <><br />by {meta.publishedBy}</> : null}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.5 }}>
                Affordability workbook  </div>
            )}

            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls"
              onChange={onPick} style={{ display: "none" }} />

            {canPublish && (
              <button onClick={() => fileRef.current?.click()} disabled={busy === "upload"}
                style={{ ...primaryBtn, width: "100%", marginTop: 10,
                  opacity: busy === "upload" ? 0.6 : 1,
                  cursor: busy === "upload" ? "wait" : "pointer" }}>
                {busy === "upload" ? "Publishing…" : "⬆ Upload new workbook"}
              </button>
            )}
            <button onClick={() => { load(); refreshMeta(); }}
              style={{ ...ghostBtn, width: "100%", marginTop: 6 }}>↻ Reload</button>

            <div style={{ fontSize: 10, color: T.textDim, marginTop: 8, lineHeight: 1.5 }}>
              {canPublish
                ? "The file is checked here before publishing. Everyone sees the new one from their next load; the old one is kept as a version."
                : "This dashboard is published by the affordability team."}
            </div>
          </div>

          {notice && (
            <div style={{ marginTop: 10, fontSize: 11, lineHeight: 1.5, padding: "8px 10px",
              borderRadius: 8, background: notice.kind === "ok" ? "#EAF6EC" : T.accentSoft,
              color: notice.kind === "ok" ? T.pos : T.accentDark,
              border: `1px solid ${notice.kind === "ok" ? "#CBE6D2" : "#F0CCC3"}` }}>
              {notice.text}
            </div>
          )}

          <Label>History</Label>
          {versions === null ? (
            <button onClick={loadVersions} style={{ ...ghostBtn, width: "100%" }}>Show published versions</button>
          ) : versions.length === 0 ? (
            <div style={{ fontSize: 11, color: T.textDim, padding: "2px 2px 8px" }}>No versions published yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {versions.map(v => (
                <div key={v.versionId} style={{ background: T.surface, border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>
                    {v.versionId}{v.isCurrent && <span style={{ color: T.accent }}> · current</span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, wordBreak: "break-all" }}>
                    {v.fileName} {v.publishedBy ? `· ${v.publishedBy}` : ""}
                  </div>
                  {canPublish && !v.isCurrent && (
                    <button onClick={() => rollback(v.versionId)} disabled={busy.startsWith("rollback")}
                      style={{ ...ghostBtn, marginTop: 6, padding: "4px 10px", fontSize: 10.5 }}>
                      {busy === `rollback:${v.versionId}` ? "Restoring…" : "Restore"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── User footer ─── */}
        <div style={{ borderTop: `1px solid ${T.border}`, padding: "12px 14px",
          display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            background: T.accentSoft, color: T.accentDark, fontWeight: 700, fontSize: 12,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.name || user?.email || "Signed in"}
            </div>
          </div>
          <button onClick={logout} style={{ ...ghostBtn, padding: "4px 10px", fontSize: 10.5 }}>Sign out</button>
        </div>
      </aside>

      {/* ═══ MAIN ═══ */}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <header style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`,
          background: T.surface, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle sidebar" style={ghostBtn}>☰</button>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Affordability Dashboard</div>
          <div style={{ marginLeft: "auto", fontSize: 11, color: T.textDim }}>
            {meta?.version ? `v${meta.version}` : "Affordability workbook"}
          </div>
        </header>

        <div style={{ flex: 1, overflowY: "auto", background: T.bgAlt }}>
          {state.status === "loading" && <Splash title="Loading the affordability dashboard…" sub={`Reading ${WORKBOOK}`} />}
          {state.status === "error" && (
            <Splash title="Could not load the dashboard" sub={state.error}
              action={<Btn onClick={load}>Try again</Btn>} />
          )}
          {state.status === "ready" && (
            <DDRDashboard data={state.data} onAskAI={onAskAI}
              onReset={canPublish ? () => fileRef.current?.click() : undefined} />
          )}
        </div>
      </main>
    </div>
  );
}

/* ── Small pieces ────────────────────────────────────────────────────── */
const primaryBtn = {
  background: T.accent, border: "none", color: "#fff", padding: "8px 14px",
  borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: T.sans,
};
const ghostBtn = {
  background: T.surface, border: `1px solid ${T.border}`, color: T.textMid,
  padding: "6px 12px", borderRadius: 8, fontSize: 11.5, fontWeight: 600,
  cursor: "pointer", fontFamily: T.sans,
};

const Label = ({ children }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: 0.8,
    textTransform: "uppercase", margin: "14px 2px 6px" }}>{children}</div>
);

const Btn = ({ children, ...p }) => <button {...p} style={primaryBtn}>{children}</button>;

function SideBtn({ children, onClick, active, icon }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", padding: "11px 14px", borderRadius: T.radiusSm,
      background: active ? `linear-gradient(135deg, ${T.accent}, ${T.accentDark})` : T.surface,
      color: active ? "#fff" : T.text, border: `1px solid ${active ? T.accent : T.border}`,
      cursor: active ? "default" : "pointer", fontSize: 13.5, fontWeight: 600,
      display: "flex", alignItems: "center", gap: 10, fontFamily: T.sans,
      boxShadow: active ? T.shadowMd : T.shadow,
    }}>{icon} {children}</button>
  );
}

function Splash({ title, sub, action }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 40 }}>
      <div style={{ textAlign: "center", maxWidth: 520 }}>
        <div style={{ width: 34, height: 34, margin: "0 auto 16px",
          border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%",
          animation: "aff-spin 0.8s linear infinite" }} />
        <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{title}</div>
        {sub && <div style={{ color: T.textDim, fontSize: 11.5, marginTop: 6, lineHeight: 1.6 }}>{sub}</div>}
        {action && <div style={{ marginTop: 16 }}>{action}</div>}
        <style>{"@keyframes aff-spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    </div>
  );
}